import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DefaultOptionDetail from "src/components/NewComponents/Exam/OptionDetails/DefaultOptionDetail";
import { AiDiscussion } from "src/components/NewComponents/Exam/OptionDetails/Discussion/AiDiscussion";
import { OwnDiscussion } from "src/components/NewComponents/Exam/OptionDetails/Discussion/OwnDiscussion";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import DiscussionSkeleton from "src/components/NewComponents/Skeletons/Discussion";
import { useSlot } from "src/components/Slots/SlotsContext";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import { useFontSize } from "src/hooks/useFontSize";
import DiscussionIcon from "src/images/Icons/newIcons/exam/options/discussion.svg";
import { useQuestionsListSettings } from "src/reducers/questions";

// initialTab: zakładka na start (admin: lista pytań), inaczej liczona z details
// aiAutoGenerate=false: zakładka AI zawsze dostępna, bez generowania na wejściu (przycisk „Wygeneruj”)
function Discussion({
	settings,
	details,
	question,
	dispatch,
	actions,
	initialTab = undefined,
	aiAutoGenerate = true,
}) {
	const { aiFilterActive } = useQuestionsListSettings();
	// slot edycji (tylko admin) odblokowuje zakladke autorska i zawsze na niej otwiera
	const editSlot = useSlot("ownDiscussion");
	const RagDiscussion = useSlot("ragDiscussion");
	const hasDiscussion = details.hasDiscussion || !!editSlot;
	const preferAi = aiFilterActive && details.hasAiDiscussion && !editSlot;
	// admin z filtrem AI (np. ?isAiUnapproved=1) startuje na RAG, jesli jest
	const preferRag =
		aiFilterActive && !!RagDiscussion && !!details.hasRagDiscussion;
	const defaultTab =
		initialTab ?? (preferRag ? 2 : hasDiscussion && !preferAi ? 0 : 1);

	const [currentTab, setCurrentTab] = useState(defaultTab);
	// "Edytuj" na omowieniu typu rag otwiera zakladke RAG od razu w edycji
	const [ragEdit, setRagEdit] = useState(false);

	const { fontSizeClass } = useFontSize();
	const { t } = useTranslation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset zakladki przy zmianie pytania
	useEffect(() => {
		setCurrentTab(defaultTab);
		setRagEdit(false);
	}, [question.id, details, defaultTab]);

	if (!settings.discussion) {
		return null;
	}

	return (
		<DefaultOptionDetail
			dispatch={dispatch}
			actions={actions}
			renderContent={() => {
				if (hasLimitedAccess()) {
					return <ShortLimitedAccess />;
				}

				return (
					<div className="flex flex-col gap-16">
						<Tabs
							className="tabs"
							value={currentTab}
							onChange={(_e, newValue) => {
								setRagEdit(false);
								setCurrentTab(newValue);
							}}
						>
							<Tab
								value={0}
								disabled={!hasDiscussion}
								className="tab size-16 first-letter-upper"
								label={t("exam.discussionAuthor")}
							/>
							<Tab
								value={1}
								disabled={aiAutoGenerate && !details.hasAiDiscussion}
								className="tab size-16 text-transform-none"
								label={t("exam.discussionAi")}
							/>
							{RagDiscussion && (
								<Tab
									value={2}
									disabled={!details.hasRagDiscussion}
									className="tab size-16 text-transform-none"
									label={t("admin.discussion.rag.tab", "Omówienie RAG")}
								/>
							)}
						</Tabs>
						{currentTab === 0 && (
							<OwnDiscussion
								questionId={question.id}
								onEditRag={
									RagDiscussion
										? () => {
												setRagEdit(true);
												setCurrentTab(2);
											}
										: undefined
								}
							/>
						)}
						{currentTab === 1 && (
							<AiDiscussion
								questionId={question.id}
								autoGenerate={aiAutoGenerate}
							/>
						)}
						{currentTab === 2 && RagDiscussion && (
							<RagDiscussion
								key={question.id}
								questionId={question.id}
								startInEdit={ragEdit}
							/>
						)}
					</div>
				);
			}}
			isOpen={settings.discussion}
			actionKey="discussion"
			renderHeader={() => (
				<div className="flex-center-row gap-8">
					<DiscussionIcon className="icon-normal" />
					<span className={fontSizeClass}>{t("exam.discussion")}</span>
				</div>
			)}
			Skeleton={<DiscussionSkeleton />}
		/>
	);
}

export default Discussion;
